import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  BookOpen,
  Clock,
  Copy,
  FileText,
  GitCompare,
  Images,
  LayoutGrid,
  Pencil,
  Plus,
  ShoppingCart,
  Sparkles,
  Trash2,
  Truck,
  User,
} from 'lucide-react';
import {
  addPackageItem,
  copyPackageItems,
  createPackage,
  deletePackage,
  fetchClient,
  fetchPackagesByProject,
  fetchPhaseInput,
  fetchProject,
  packagePricingOverrideFromPackage,
  removePackageItem,
  SECTION_LABEL,
  updatePackage,
  updateProjectClient,
} from '@/lib/api';
import { ClientPicker } from '@/components/ClientPicker';
import { ProjectPhaseBadge } from '@/components/ProjectPhaseBadge';
import { OrderConfirmationPanel } from '@/components/OrderConfirmationPanel';
import type { PhaseInput } from '@/lib/projectPhase';
import type { Client } from '@/lib/types';
import type { PackageSection } from '@/lib/api';
import { CockpitTopBar } from '@/components/CockpitTopBar';
import { CustomerContext } from '@/components/CustomerContext';
import { DeliveryPanel } from '@/components/DeliveryPanel';
import { ExportButtons } from '@/components/ExportButtons';
import { PresentationFlow } from '@/components/PresentationFlow';
import { BrandAssetUpload } from '@/components/BrandAssetUpload';
import { ComparisonViewer } from '@/components/ComparisonViewer';
import { CloneStep } from '@/components/CloneStep';
import { SideDrawer } from '@/components/SideDrawer';
import { TranscriptCard } from '@/components/TranscriptCard';
import { CatalogSearch } from '@/components/CatalogSearch';
import { PackageSections } from '@/components/PackageSections';
import { WizardBuilder } from '@/components/WizardBuilder';
import type { GiftPackage, GiftProduct, Project } from '@/lib/types';

type Mode = 'wizard' | 'cockpit';

export function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const [project, setProject] = useState<Project | null>(null);
  const [client, setClient] = useState<Client | null>(null);
  const [editClient, setEditClient] = useState(false);
  const [packages, setPackages] = useState<GiftPackage[]>([]);
  const [phaseInput, setPhaseInput] = useState<PhaseInput | null>(null);
  const [activePkgId, setActivePkgId] = useState<string | null>(null);
  const [catalogSection, setCatalogSection] = useState<PackageSection | 'alles'>('alles');
  const [catalogLocked, setCatalogLocked] = useState(false);
  const [mode, setMode] = useState<Mode | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [deliveryOpen, setDeliveryOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [contextOpen, setContextOpen] = useState(false);
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const [presentationOpen, setPresentationOpen] = useState(false);
  const [brandOpen, setBrandOpen] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [loadConfigOpen, setLoadConfigOpen] = useState(false);

  const reload = useCallback(async () => {
    if (!id) return;
    try {
      const [p, pkgs] = await Promise.all([fetchProject(id), fetchPackagesByProject(id)]);
      setProject(p);
      setPackages(pkgs);
      fetchPhaseInput(p, pkgs)
        .then(setPhaseInput)
        .catch(() => setPhaseInput(null));
      if (p.client_id) {
        fetchClient(p.client_id)
          .then(setClient)
          .catch(() => setClient(null));
      } else {
        setClient(null);
      }
      // Respecteer huidige selectie als die nog bestaat — voorkomt dat na een copy/add
      // de tab terug-springt naar pkgs[0]. Alleen fallback bij eerste load of als
      // de huidige selectie weg is.
      setActivePkgId((current) => {
        if (current && pkgs.some((pk) => pk.id === current)) return current;
        const preferred = searchParams.get('package');
        if (preferred && pkgs.some((pk) => pk.id === preferred)) return preferred;
        return pkgs[0]?.id ?? null;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Laden mislukt');
    } finally {
      setLoading(false);
    }
  }, [id, searchParams]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const activePkg = useMemo(
    () => packages.find((p) => p.id === activePkgId) ?? null,
    [packages, activePkgId]
  );

  // Default mode bij eerste load: wizard bij leeg pakket, cockpit bij gevuld
  useEffect(() => {
    if (mode !== null || !activePkg) return;
    const itemCount = activePkg.items?.length ?? 0;
    setMode(itemCount === 0 ? 'wizard' : 'cockpit');
  }, [activePkg, mode]);

  async function handleAddProduct(product: GiftProduct) {
    if (!activePkg) return;
    await addPackageItem({
      package_id: activePkg.id,
      gift_product_id: product.id,
      quantity_per_package: 1,
      selling_price_per_item: product.unit_purchase_price * 1.43,
    });
    await reload();
  }

  async function handleRemove(itemId: string) {
    await removePackageItem(itemId);
    await reload();
  }

  function handleAddSection(section: PackageSection) {
    setCatalogSection(section);
    setCatalogLocked(true);
    setCatalogOpen(true);
  }

  function handleClearSectionFilter() {
    setCatalogSection('alles');
    setCatalogLocked(false);
  }

  if (loading) {
    return <div className="text-sm text-bmade-500">Laden...</div>;
  }
  if (error || !project) {
    return (
      <div className="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300">
        {error ?? 'Project niet gevonden'}
      </div>
    );
  }

  return (
    <div className="flex min-h-full flex-col gap-4 pb-12">
      <header className="flex items-center justify-between">
        <div>
          <Link
            to="/projecten"
            className="mb-1 inline-flex items-center gap-1 text-xs text-bmade-500 transition-colors hover:text-bmade-800 dark:text-bmade-400 dark:hover:text-white"
          >
            <ArrowLeft className="h-3 w-3" /> Projecten
          </Link>
          <h1 className="text-2xl font-semibold text-bmade-950 dark:text-white">{project.name}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-bmade-600 dark:text-bmade-400">
            <span className="flex items-center gap-2">
              {project.package_count_planned ?? '?'} pakketten ·
              {phaseInput ? (
                <ProjectPhaseBadge input={phaseInput} />
              ) : (
                <span>status {project.phase}</span>
              )}
            </span>
            <div className="flex items-center gap-2">
              <User className="h-3 w-3 text-bmade-500" />
              {editClient ? (
                <div className="flex w-64 items-center gap-1">
                  <ClientPicker
                    value={client}
                    onChange={async (c) => {
                      await updateProjectClient(project.id, c?.id ?? null);
                      setClient(c);
                      setEditClient(false);
                      void reload();
                    }}
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setEditClient(false)}
                    className="text-[10px] text-bmade-500 hover:text-bmade-900"
                  >
                    annuleer
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setEditClient(true)}
                  className="group inline-flex items-center gap-1 rounded px-1 py-0.5 hover:bg-bmade-100 dark:hover:bg-bmade-800"
                >
                  <span className="font-medium text-bmade-800 dark:text-bmade-200">
                    {client?.company_name ?? 'Geen klant gekoppeld'}
                  </span>
                  <Pencil className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" />
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to={`/projecten/${project.id}/inkoop`}
            className="inline-flex items-center gap-1 rounded-md border border-bmade-200 px-3 py-1.5 text-xs font-medium text-bmade-700 hover:border-accent-400 hover:bg-accent-50 hover:text-accent-700 dark:border-bmade-700 dark:text-bmade-300 dark:hover:border-accent-400 dark:hover:bg-accent-500/10 dark:hover:text-accent-300"
          >
            <ShoppingCart className="h-3 w-3" />
            Inkoop
          </Link>
          <ModeToggle mode={mode ?? 'wizard'} onChange={setMode} />
        </div>
      </header>

      {phaseInput && phaseInput.definitiefCount > 0 && (
        <div className="shrink-0">
          <OrderConfirmationPanel project={project} onChanged={() => void reload()} />
        </div>
      )}

      {mode === 'wizard' && activePkg ? (
        <WizardBuilder
          pkg={activePkg}
          project={project}
          client={client}
          clientName={client?.company_name}
          onAdd={handleAddProduct}
          onRemove={handleRemove}
          onFinish={() => setMode('cockpit')}
          onProjectChanged={() => void reload()}
          initialStepId={
            searchParams.get('step') === 'basis'
              ? 'basis'
              : searchParams.get('step') === 'gesprek'
                ? 'gesprek'
                : undefined
          }
        />
      ) : (
        <div data-testid="project-cockpit-layout" className="flex flex-col gap-4">
          {activePkg ? (
            <>
              <CockpitTopBar pkg={activePkg} onChanged={() => void reload()} />

              <div className="flex shrink-0 flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setCatalogOpen(true)}
                  className="inline-flex items-center gap-1.5 rounded-md border border-bmade-200 px-3 py-1.5 text-xs font-medium text-bmade-700 hover:border-accent-400 hover:text-accent-700 dark:border-bmade-700 dark:text-bmade-300"
                >
                  <BookOpen className="h-3 w-3" />
                  Catalogus
                </button>
                <button
                  type="button"
                  onClick={() => setTranscriptOpen(true)}
                  className="inline-flex items-center gap-1.5 rounded-md border border-bmade-200 px-3 py-1.5 text-xs font-medium text-bmade-700 hover:border-bmade-400 dark:border-bmade-700 dark:text-bmade-300"
                >
                  <Sparkles className="h-3 w-3" />
                  Gesprek + AI
                </button>
                <button
                  type="button"
                  onClick={() => setContextOpen(true)}
                  className="inline-flex items-center gap-1.5 rounded-md border border-bmade-200 px-3 py-1.5 text-xs font-medium text-bmade-700 hover:border-bmade-400 dark:border-bmade-700 dark:text-bmade-300"
                >
                  <Clock className="h-3 w-3" />
                  Klant-historie
                </button>
                <button
                  type="button"
                  onClick={() => setDeliveryOpen(true)}
                  className="inline-flex items-center gap-1.5 rounded-md border border-bmade-200 px-3 py-1.5 text-xs font-medium text-bmade-700 hover:border-bmade-400 dark:border-bmade-700 dark:text-bmade-300"
                >
                  <Truck className="h-3 w-3" />
                  Uitlevering
                </button>
                <button
                  type="button"
                  onClick={() => setBrandOpen(true)}
                  className="inline-flex items-center gap-1.5 rounded-md border border-bmade-200 px-3 py-1.5 text-xs font-medium text-bmade-700 hover:border-bmade-400 dark:border-bmade-700 dark:text-bmade-300"
                  title="Huisstijl-voorbeeld van de klant uploaden"
                >
                  <Images className="h-3 w-3" />
                  Huisstijl
                </button>
                <button
                  type="button"
                  onClick={() => setCompareOpen(true)}
                  className="inline-flex items-center gap-1.5 rounded-md border border-bmade-200 px-3 py-1.5 text-xs font-medium text-bmade-700 hover:border-bmade-400 dark:border-bmade-700 dark:text-bmade-300"
                  title="Dit pakket naast een eerder pakket leggen"
                >
                  <GitCompare className="h-3 w-3" />
                  Vergelijk
                </button>
                <button
                  type="button"
                  onClick={() => setLoadConfigOpen(true)}
                  className="inline-flex items-center gap-1.5 rounded-md border border-bmade-200 px-3 py-1.5 text-xs font-medium text-bmade-700 hover:border-bmade-400 dark:border-bmade-700 dark:text-bmade-300"
                  title="Items van een eerder pakket in dit pakket inladen"
                >
                  <Copy className="h-3 w-3" />
                  Inladen
                </button>
                <button
                  type="button"
                  onClick={() => setPresentationOpen(true)}
                  className="ml-auto inline-flex items-center gap-1.5 rounded-md bg-accent-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-600"
                  title="Klant-presentatie: foto's + verhalende tekst + PDF"
                >
                  <FileText className="h-3 w-3" />
                  PDF voor klant
                </button>
                <button
                  type="button"
                  onClick={() => setExportOpen(true)}
                  className="inline-flex items-center gap-1.5 rounded-md border border-bmade-200 px-3 py-1.5 text-xs font-medium text-bmade-700 hover:border-accent-400 hover:text-accent-700 dark:border-bmade-700 dark:text-bmade-300"
                  title="Inkoop-PDF's per leverancier + mail-drafts"
                >
                  <Sparkles className="h-3 w-3" />
                  Inkoop-exports
                </button>
              </div>

              <section className="rounded-xl border border-bmade-200 bg-white p-6 dark:border-bmade-800 dark:bg-bmade-900/40">
                <VariantBar
                  project={project}
                  packages={packages}
                  activePkg={activePkg}
                  onSelect={setActivePkgId}
                  onChanged={() => void reload()}
                />
                <PackageSections
                  pkg={activePkg}
                  onRemove={handleRemove}
                  onAddSection={handleAddSection}
                  onChanged={() => void reload()}
                />
              </section>
            </>
          ) : (
            <p className="text-sm text-bmade-500">Geen pakket geselecteerd.</p>
          )}

          <SideDrawer
            open={catalogOpen}
            onClose={() => setCatalogOpen(false)}
            title={
              catalogLocked && catalogSection !== 'alles'
                ? `Catalogus · ${SECTION_LABEL[catalogSection as PackageSection]}`
                : 'Catalogus'
            }
            width="md"
          >
            <CatalogSearch
              onAdd={handleAddProduct}
              disabled={!activePkg}
              initialSection={catalogSection}
              lockSection={catalogLocked}
            />
            {catalogLocked && (
              <button
                type="button"
                onClick={handleClearSectionFilter}
                className="mt-3 w-full text-center text-[11px] uppercase tracking-wider text-bmade-500 hover:text-bmade-900 dark:text-bmade-400 dark:hover:text-white"
              >
                Filter resetten naar Alles
              </button>
            )}
          </SideDrawer>

          <SideDrawer
            open={transcriptOpen}
            onClose={() => setTranscriptOpen(false)}
            title="Gesprek + AI-tips"
            width="lg"
          >
            <TranscriptCard project={project} onChanged={() => void reload()} />
            {(project.ai_summary || (project.ai_tips?.product_suggestions ?? []).length > 0) && (
              <div className="mt-4 space-y-3">
                {project.ai_summary && (
                  <section>
                    <h3 className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-bmade-600 dark:text-bmade-400">
                      Samenvatting
                    </h3>
                    <p className="text-sm leading-relaxed text-bmade-800 dark:text-bmade-200">
                      {project.ai_summary}
                    </p>
                  </section>
                )}
                {(project.ai_tips?.dietary_warnings ?? []).length > 0 && (
                  <section>
                    <h3 className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-amber-600 dark:text-amber-400">
                      Allergie / dieet
                    </h3>
                    <div className="flex flex-wrap gap-1">
                      {project.ai_tips!.dietary_warnings!.map((w, i) => (
                        <span
                          key={i}
                          className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800 dark:bg-amber-500/20 dark:text-amber-200"
                        >
                          {w}
                        </span>
                      ))}
                    </div>
                  </section>
                )}
              </div>
            )}
          </SideDrawer>

          <SideDrawer
            open={contextOpen}
            onClose={() => setContextOpen(false)}
            title="Klant-historie en favorieten"
            width="md"
          >
            <CustomerContext
              clientId={activePkg?.client_id ?? project.client_id}
              currentPackageId={activePkg?.id ?? null}
              onAdd={handleAddProduct}
            />
          </SideDrawer>

          <SideDrawer
            open={deliveryOpen}
            onClose={() => setDeliveryOpen(false)}
            title="Uitlevering en handling"
            width="lg"
          >
            {activePkg && <DeliveryPanel pkg={activePkg} onChanged={() => void reload()} />}
          </SideDrawer>

          <SideDrawer
            open={exportOpen}
            onClose={() => setExportOpen(false)}
            title="Exports en mail-draft"
            width="md"
          >
            {activePkg && <ExportButtons project={project} pkg={activePkg} client={client} />}
          </SideDrawer>

          <SideDrawer
            open={brandOpen}
            onClose={() => setBrandOpen(false)}
            title="Huisstijl-voorbeelden"
            width="md"
          >
            <BrandAssetUpload
              projectId={project.id}
              urls={project.brand_asset_urls ?? []}
              onChange={(urls) => setProject({ ...project, brand_asset_urls: urls })}
            />
          </SideDrawer>

          <SideDrawer
            open={compareOpen}
            onClose={() => setCompareOpen(false)}
            title="Pakketten vergelijken"
            width="lg"
          >
            {activePkg && <ComparisonViewer currentPkg={activePkg} excludeProjectId={project.id} />}
          </SideDrawer>

          <SideDrawer
            open={loadConfigOpen}
            onClose={() => setLoadConfigOpen(false)}
            title="Bestaande configuratie inladen"
            width="lg"
          >
            {activePkg && (
              <CloneStep
                pkg={activePkg}
                onCloned={() => {
                  setLoadConfigOpen(false);
                  void reload();
                }}
              />
            )}
          </SideDrawer>

          <SideDrawer
            open={presentationOpen}
            onClose={() => setPresentationOpen(false)}
            title="Klant-presentatie"
            width="lg"
          >
            {activePkg && (
              <PresentationFlow
                project={project}
                pkg={activePkg}
                client={client}
                onChanged={() => void reload()}
              />
            )}
          </SideDrawer>
        </div>
      )}
    </div>
  );
}

function ModeToggle({ mode, onChange }: { mode: Mode; onChange: (m: Mode) => void }) {
  return (
    <div className="inline-flex rounded-md border border-bmade-200 bg-white p-0.5 text-xs dark:border-bmade-800 dark:bg-bmade-900">
      <button
        type="button"
        onClick={() => onChange('wizard')}
        className={`inline-flex items-center gap-1.5 rounded px-3 py-1.5 font-medium transition-colors ${
          mode === 'wizard'
            ? 'bg-accent-500 text-white'
            : 'text-bmade-600 hover:text-bmade-900 dark:text-bmade-400 dark:hover:text-white'
        }`}
      >
        <Sparkles className="h-3 w-3" />
        Wizard
      </button>
      <button
        type="button"
        onClick={() => onChange('cockpit')}
        className={`inline-flex items-center gap-1.5 rounded px-3 py-1.5 font-medium transition-colors ${
          mode === 'cockpit'
            ? 'bg-accent-500 text-white'
            : 'text-bmade-600 hover:text-bmade-900 dark:text-bmade-400 dark:hover:text-white'
        }`}
      >
        <LayoutGrid className="h-3 w-3" />
        Cockpit
      </button>
    </div>
  );
}

// BudgetPanel staat nu in components/BudgetPanel.tsx — wordt geïmporteerd boven.

const VARIANT_STATUS_OPTIONS: {
  value: NonNullable<GiftPackage['variant_status']>;
  label: string;
  tone: string;
}[] = [
  {
    value: 'definitief',
    label: 'Definitief',
    tone: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300',
  },
  {
    value: 'alternatief',
    label: 'Alternatief',
    tone: 'bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-300',
  },
  {
    value: 'concept',
    label: 'Concept',
    tone: 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300',
  },
];

function VariantBar({
  project,
  packages,
  activePkg,
  onSelect,
  onChanged,
}: {
  project: Project;
  packages: GiftPackage[];
  activePkg: GiftPackage | null;
  onSelect: (id: string) => void;
  onChanged: () => void | Promise<void>;
}) {
  const [editingLabelId, setEditingLabelId] = useState<string | null>(null);
  const [labelDraft, setLabelDraft] = useState('');
  const [busy, setBusy] = useState<null | 'add' | 'copy'>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  async function addVariant() {
    if (!activePkg || busy) return;
    setBusy('add');
    setErrorMsg(null);
    try {
      const newLabel = `Variant ${packages.length + 1}`;
      const pkg = await createPackage({
        project_id: project.id,
        client_id: activePkg.client_id,
        name: `${project.name} — ${newLabel}`,
        quantity: activePkg.quantity,
        budget_per_package: activePkg.budget_per_package,
        budget_type: activePkg.budget_type,
        variant_label: newLabel,
        variant_status: 'alternatief',
      });
      await onChanged();
      onSelect(pkg.id);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Variant toevoegen mislukt');
    } finally {
      setBusy(null);
    }
  }

  async function duplicateVariant() {
    if (!activePkg || busy) return;
    setBusy('copy');
    setErrorMsg(null);
    try {
      const newLabel = `${activePkg.variant_label ?? 'Variant'} kopie`;
      const pkg = await createPackage({
        project_id: project.id,
        client_id: activePkg.client_id,
        name: `${project.name} — ${newLabel}`,
        quantity: activePkg.quantity,
        budget_per_package: activePkg.budget_per_package,
        budget_type: activePkg.budget_type,
        variant_label: newLabel,
        variant_status: 'alternatief',
        pricing: packagePricingOverrideFromPackage(activePkg),
      });
      await copyPackageItems(activePkg.id, pkg.id);
      await onChanged();
      onSelect(pkg.id);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Variant kopiëren mislukt');
    } finally {
      setBusy(null);
    }
  }

  async function deleteVariant(pkgId: string) {
    if (packages.length <= 1) return;
    if (!window.confirm('Variant verwijderen? Dit kan niet ongedaan worden gemaakt.')) return;
    await deletePackage(pkgId);
    if (activePkg?.id === pkgId) {
      const remaining = packages.filter((p) => p.id !== pkgId);
      if (remaining.length > 0) onSelect(remaining[0].id);
    }
    onChanged();
  }

  async function setStatus(pkgId: string, status: NonNullable<GiftPackage['variant_status']>) {
    await updatePackage(pkgId, { variant_status: status });
    onChanged();
  }

  async function commitLabel() {
    if (!editingLabelId) return;
    await updatePackage(editingLabelId, { variant_label: labelDraft || 'Variant' });
    setEditingLabelId(null);
    onChanged();
  }

  return (
    <div className="mb-3 space-y-2">
      <div className="flex flex-wrap items-center gap-1">
        {packages.map((p) => {
          const active = p.id === activePkg?.id;
          const isEditing = editingLabelId === p.id;
          return (
            <div key={p.id} className="group inline-flex items-center">
              {isEditing ? (
                <input
                  autoFocus
                  value={labelDraft}
                  onChange={(e) => setLabelDraft(e.target.value)}
                  onBlur={commitLabel}
                  onKeyDown={(e) => e.key === 'Enter' && commitLabel()}
                  className="rounded-md border border-accent-400 bg-white px-2 py-1 text-xs text-bmade-950 outline-none dark:bg-bmade-950 dark:text-white"
                />
              ) : (
                <button
                  type="button"
                  onClick={() => onSelect(p.id)}
                  onDoubleClick={() => {
                    setEditingLabelId(p.id);
                    setLabelDraft(p.variant_label ?? 'Variant');
                  }}
                  className={`rounded-l-md border px-3 py-1 text-xs font-medium transition-colors ${
                    active
                      ? 'border-accent-400 bg-accent-500/15 text-accent-700 dark:bg-accent-500/15 dark:text-accent-300'
                      : 'border-bmade-200 bg-white text-bmade-700 hover:border-bmade-400 dark:border-bmade-700 dark:bg-bmade-950 dark:text-bmade-300'
                  }`}
                  title="Klik om te kiezen, dubbelklik om naam te wijzigen"
                >
                  {p.variant_label ?? 'Standaard'}
                </button>
              )}
              {packages.length > 1 && !isEditing && (
                <button
                  type="button"
                  onClick={() => void deleteVariant(p.id)}
                  title="Variant verwijderen"
                  className={`rounded-r-md border-y border-r px-1 py-1 text-bmade-400 opacity-0 transition-opacity group-hover:opacity-100 hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-500/10 dark:hover:text-red-400 ${
                    active ? 'border-accent-400' : 'border-bmade-200 dark:border-bmade-700'
                  }`}
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              )}
            </div>
          );
        })}
        <button
          type="button"
          onClick={addVariant}
          disabled={busy !== null}
          title="Nieuwe lege variant"
          className="inline-flex items-center gap-1 rounded-md border border-dashed border-bmade-300 px-2 py-1 text-xs text-bmade-500 hover:border-accent-400 hover:bg-accent-50 hover:text-accent-700 disabled:cursor-not-allowed disabled:opacity-60 dark:border-bmade-700 dark:hover:border-accent-400 dark:hover:bg-accent-500/10 dark:hover:text-accent-300"
        >
          {busy === 'add' ? (
            <span className="text-[10px]">bezig…</span>
          ) : (
            <Plus className="h-3 w-3" />
          )}
        </button>
        {activePkg && (
          <button
            type="button"
            onClick={duplicateVariant}
            disabled={busy !== null}
            title="Kopieer huidige variant inclusief inhoud"
            className="inline-flex items-center gap-1 rounded-md border border-bmade-200 px-2 py-1 text-xs text-bmade-500 hover:border-bmade-400 hover:bg-bmade-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-bmade-700 dark:hover:border-bmade-500 dark:hover:bg-bmade-800"
          >
            {busy === 'copy' ? (
              <span className="text-[10px]">kopiëren…</span>
            ) : (
              <Copy className="h-3 w-3" />
            )}
          </button>
        )}
      </div>
      {errorMsg && (
        <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300">
          {errorMsg}
        </div>
      )}
      {activePkg && (
        <div className="flex items-center gap-1 text-[10px]">
          <span className="text-bmade-500">Status:</span>
          {VARIANT_STATUS_OPTIONS.map((opt) => {
            const active = (activePkg.variant_status ?? 'definitief') === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setStatus(activePkg.id, opt.value)}
                className={`rounded-full px-2 py-0.5 transition-opacity ${
                  active
                    ? opt.tone
                    : 'bg-bmade-100 text-bmade-500 hover:opacity-70 dark:bg-bmade-800'
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
